/**
 * Silent SSO (ONEsync -> ONEview handoff)
 *
 * When a user opens any ONEview URL without a verified ONEview session while their
 * browser holds an Entra ID session for our tenant (e.g. they signed in to ONEsync,
 * Office 365 or the Azure portal), sign them in without showing the ONEview login page.
 *
 * Nothing here is ONEsync-specific: the trigger is "no verified ONEview session + an
 * Entra session in the browser", so any Entra sign-in exercises the same code path.
 *
 * Flow:
 *  1. MSAL already holds an account in this tab (e.g. returning from a redirect) -> use it.
 *  2. ssoSilent() in a hidden iframe.
 *  3. Microsoft needs one more step from a signed-in user (pick an account, MFA,
 *     confirmation) -> redirect to Microsoft for that step, not to the ONEview login page.
 *  4. The iframe saw no session or failed (third-party cookies blocked, timeout) ->
 *     top-level loginRedirect with prompt=none, which can see first-party cookies and
 *     never shows Microsoft UI.
 *  5. Otherwise the user sees the normal login page.
 */

import {
  AccountInfo,
  AuthError,
  InteractionRequiredAuthError,
  IPublicClientApplication,
} from "@azure/msal-browser";
import type { Dispatch } from "@reduxjs/toolkit";
import type { User } from "@/store/slices/authSlice";
import { loginRequest, msalConfig } from "@/config/azureEntraIdConfig";
import { establishSession, getCurrentMsalAccount } from "@/utils/sessionIdentity";

export { mapAccountToUser } from "@/utils/sessionIdentity";

export const LOGIN_INITIATED_KEY = "oneview_login_initiated";
// Set after an explicit logout so /login does not silently sign the user back in
export const SKIP_SILENT_SSO_KEY = "oneview_skip_silent_sso";
// Last redirect made by this flow and its outcome, guards against redirect loops
export const SSO_STAGE_KEY = "oneview_sso_stage";
export const SSO_REDIRECT_COOLDOWN_MS = 2 * 60 * 1000;

// Optional hints a launching app (e.g. ONEsync) may append to the ONEview URL
export const SSO_HINT_PARAMS = ["login_hint", "domain_hint", "sid"] as const;

export type SilentSsoResult = "authenticated" | "redirecting" | "unauthenticated";

/**
 * What Microsoft needs before it can sign the user in:
 * - no_session: nobody is signed in (or the iframe could not see the session cookie)
 * - select_account: several accounts are signed in (AADSTS16000)
 * - interaction: the user is signed in but must complete a step (MFA, consent, confirmation)
 */
type RequiredStep = "no_session" | "select_account" | "interaction";

interface SsoStage {
  step: "silent_redirect" | "interactive";
  at: number;
  outcome?: RequiredStep | "failed";
}

interface SsoHints {
  loginHint?: string;
  domainHint?: string;
  sid?: string;
}

/**
 * Clear the flags that suppress silent SSO; called when the user logs in manually
 */
export const clearSilentSsoFlags = () => {
  sessionStorage.removeItem(SKIP_SILENT_SSO_KEY);
  sessionStorage.removeItem(SSO_STAGE_KEY);
};

// One attempt per page load, shared by every caller (LoginForm, StrictMode)
let attemptPromise: Promise<SilentSsoResult> | null = null;
let lastResult: SilentSsoResult | null = null;

const hasAuthResponseInUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const hash = window.location.hash;
  return (
    params.has("code") ||
    params.has("state") ||
    params.has("error") ||
    /[#&](code|error)=/.test(hash)
  );
};

const classifyError = (err: unknown): RequiredStep | null => {
  if (!(err instanceof InteractionRequiredAuthError)) return null;
  if (err.errorCode === "login_required") return "no_session";
  if (/AADSTS16000/.test(`${err.errorMessage} ${err.subError}`)) return "select_account";
  return "interaction";
};

const readRecentStage = (): SsoStage | null => {
  try {
    const stage = JSON.parse(sessionStorage.getItem(SSO_STAGE_KEY) || "null") as SsoStage | null;
    return stage && Date.now() - stage.at < SSO_REDIRECT_COOLDOWN_MS ? stage : null;
  } catch {
    return null;
  }
};

const writeStage = (stage: SsoStage) =>
  sessionStorage.setItem(SSO_STAGE_KEY, JSON.stringify(stage));

/**
 * Record why a redirect made by this flow came back without an account.
 * Called from main.tsx when handleRedirectPromise rejects.
 */
export const recordRedirectError = (err: unknown) => {
  const stage = readRecentStage();
  if (!stage) return;
  writeStage({ ...stage, outcome: classifyError(err) ?? "failed" });
};

/**
 * Whether a silent attempt should run on this page load
 */
export const isSilentSsoEligible = () => {
  if (lastResult === "unauthenticated") return false;
  if (hasAuthResponseInUrl()) return false;
  return sessionStorage.getItem(SKIP_SILENT_SSO_KEY) !== "true";
};

/**
 * Read hint params from the current URL
 */
const readSsoHints = (): SsoHints => {
  const params = new URLSearchParams(window.location.search);
  const hints: SsoHints = {};
  const loginHint = params.get("login_hint");
  const domainHint = params.get("domain_hint");
  const sid = params.get("sid");
  if (loginHint) hints.loginHint = loginHint;
  if (domainHint) hints.domainHint = domainHint;
  if (sid) hints.sid = sid;
  return hints;
};

/**
 * Remove SSO hint params from a query string (hints can contain the user's UPN)
 */
export const stripSsoHints = (search: string) => {
  const params = new URLSearchParams(search);
  SSO_HINT_PARAMS.forEach((param) => params.delete(param));
  const query = params.toString();
  return query ? `?${query}` : "";
};

const getPageUrlWithoutHints = () =>
  `${window.location.origin}${window.location.pathname}${stripSsoHints(window.location.search)}`;

// Captured once at page load, before the router moves an unauthenticated user from
// e.g. /dashboard/<patient>?login_hint=... to /login
let pageLoadHints = readSsoHints();
let pageLoadUrl = getPageUrlWithoutHints();

// Dev-only trace of which branch the attempt took, to diagnose "login page shown" reports
const logSso = (message: string, detail?: unknown) => {
  if (import.meta.env.DEV) {
    // Time since page navigation started, to see where loading time goes
    const elapsed = `+${Math.round(performance.now())}ms`;
    console.info(`[Silent SSO] ${elapsed} ${message}`, ...(detail === undefined ? [] : [detail]));
  }
};

const describeError = (err: unknown) =>
  err instanceof AuthError ? `${err.errorCode}: ${err.errorMessage}` : err;

// The hidden iframe returns to the app's registered redirect URI; main.tsx does not boot
// the app inside that iframe, so no separate blank page (or extra registration) is needed
const getSilentRedirectUri = () =>
  import.meta.env.VITE_AZURE_SILENT_REDIRECT_URI || msalConfig.auth.redirectUri;

const completeLogin = (
  instance: IPublicClientApplication,
  dispatch: Dispatch,
  account: AccountInfo,
  previousUser: User | null
): SilentSsoResult => {
  if (!establishSession(instance, dispatch, account, previousUser)) {
    logSso("Account rejected (unexpected tenant)");
    return "unauthenticated";
  }
  // Lets the existing sync effect in useAzureAuth record the USER_LOGIN audit event
  sessionStorage.setItem(LOGIN_INITIATED_KEY, "true");
  sessionStorage.removeItem(SSO_STAGE_KEY);
  return "authenticated";
};

const redirectToMicrosoft = async (
  instance: IPublicClientApplication,
  step: SsoStage["step"],
  prompt?: "none" | "select_account"
): Promise<SilentSsoResult> => {
  try {
    writeStage({ step, at: Date.now() });
    sessionStorage.setItem(LOGIN_INITIATED_KEY, "true");
    logSso(`Redirecting to Microsoft (${step}${prompt ? `, prompt=${prompt}` : ""})`);
    await instance.loginRedirect({
      ...loginRequest,
      ...pageLoadHints,
      ...(prompt && { prompt }),
      // Come back to the page the user originally opened, not /login
      redirectStartPage: pageLoadUrl,
    });
    return "redirecting";
  } catch (err) {
    sessionStorage.removeItem(LOGIN_INITIATED_KEY);
    console.warn("Silent SSO redirect failed:", err);
    return "unauthenticated";
  }
};

// The user is signed in to Microsoft but it needs one more step from them
const redirectForStep = (instance: IPublicClientApplication, step: RequiredStep) =>
  redirectToMicrosoft(
    instance,
    "interactive",
    step === "select_account" ? "select_account" : undefined
  );

const attemptSilentSso = async (
  instance: IPublicClientApplication,
  dispatch: Dispatch,
  previousUser: User | null
): Promise<SilentSsoResult> => {
  if (!isSilentSsoEligible()) {
    logSso("Skipped: not eligible", {
      skippedAfterLogout: sessionStorage.getItem(SKIP_SILENT_SSO_KEY) === "true",
      authResponseInUrl: hasAuthResponseInUrl(),
    });
    return "unauthenticated";
  }

  // 1. MSAL already has an account in this tab (e.g. we just came back from a redirect)
  const existingAccount = getCurrentMsalAccount(instance);
  if (existingAccount) {
    logSso("Signed in with account already in MSAL cache");
    return completeLogin(instance, dispatch, existingAccount, previousUser);
  }

  // A redirect from this flow just came back without an account. The record only guards
  // the page load that returns from that redirect (preventing loops), so consume it: a
  // later refresh by the user, e.g. after signing in to Microsoft, tries again.
  const stage = readRecentStage();
  sessionStorage.removeItem(SSO_STAGE_KEY);
  if (stage) {
    if (
      stage.step === "silent_redirect" &&
      (stage.outcome === "select_account" || stage.outcome === "interaction")
    ) {
      logSso(`prompt=none redirect needs a step from the user (${stage.outcome})`);
      return redirectForStep(instance, stage.outcome);
    }
    logSso(`Skipped: the ${stage.step} redirect that just returned found no account`, stage.outcome);
    return "unauthenticated";
  }

  // 2. Hidden iframe
  try {
    logSso("Trying ssoSilent (hidden iframe)", {
      redirectUri: getSilentRedirectUri(),
      hints: Object.keys(pageLoadHints),
    });
    const response = await instance.ssoSilent({
      ...loginRequest,
      ...pageLoadHints,
      redirectUri: getSilentRedirectUri(),
    });
    if (response?.account) {
      logSso("ssoSilent succeeded");
      return completeLogin(instance, dispatch, response.account, previousUser);
    }
    logSso("ssoSilent returned no account");
    return "unauthenticated";
  } catch (err) {
    const step = classifyError(err);
    // 3. Signed in, but Microsoft needs the user to pick an account / do MFA / confirm
    if (step === "select_account" || step === "interaction") {
      logSso("ssoSilent needs a step from the user, redirecting to Microsoft", describeError(err));
      return redirectForStep(instance, step);
    }
    logSso("ssoSilent found no usable session, trying prompt=none redirect", describeError(err));
  }

  // 4. Top-level redirect without UI; sees first-party cookies when the iframe cannot
  return redirectToMicrosoft(instance, "silent_redirect", "none");
};

/**
 * Run the silent SSO attempt at most once per page load
 *
 * @param previousUser - the persisted OneView user, wiped if the Microsoft identity differs
 */
export const runSilentSso = (
  instance: IPublicClientApplication,
  dispatch: Dispatch,
  previousUser: User | null = null
): Promise<SilentSsoResult> => {
  if (!attemptPromise) {
    attemptPromise = attemptSilentSso(instance, dispatch, previousUser).then((result) => {
      lastResult = result;
      return result;
    });
  }
  return attemptPromise;
};

/**
 * Test helper: reset the per-page-load state
 */
export const resetSilentSsoState = () => {
  attemptPromise = null;
  lastResult = null;
  pageLoadHints = readSsoHints();
  pageLoadUrl = getPageUrlWithoutHints();
};
